import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, CheckCircle, Warning, ArrowClockwise, Storefront, Tag, Trash, Siren } from "@phosphor-icons/react";

const ROLES = [
  { key: "main_line_number", label: "Main line", desc: "Outbound caller ID for calls & SMS (was TELNYX_DEFAULT_FROM)." },
  { key: "fax_number", label: "Fax", desc: "Sender for outbound Cloud Fax (was TELNYX_FAX_FROM)." },
  { key: "whatsapp_number", label: "WhatsApp Business", desc: "Verified WhatsApp sender (was WHATSAPP_FROM)." },
  { key: "voice_sales_did", label: "SI Voice Seller inbound", desc: "Number that the SI Voice Seller answers on. Point its Telnyx voice webhook at the platform." },
  { key: "voice_sales_handoff_number", label: "Human handoff", desc: "Transfer target when the SI hands off a call (can be any reachable line)." },
  { key: "dialin_number", label: "Meet dial-in", desc: "PSTN dial-in number for meetings (optional)." },
];

export default function AdminNumbers() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/number-roles");
      setData(d);
      setForm({ ...d.roles });
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      ROLES.forEach((r) => { patch[r.key] = form[r.key] || ""; });
      patch.dialin_enabled = !!form.dialin_enabled;
      await api.post("/number-roles", patch);
      toast.success("Number roles saved — applied to the running app.");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="py-16 text-center text-muted-foreground" data-testid="numbers-loading">Loading…</div>;

  const accountNums = data.account_numbers || [];

  return (
    <div className="space-y-6" data-testid="admin-numbers">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Phone size={22} weight="fill" /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Number Roles</h1>
            <p className="text-sm text-muted-foreground">Assign each platform role to one of your numbers — no environment editing.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${data.telnyx_live ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>
            {data.telnyx_live ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
            Telnyx {data.telnyx_live ? "LIVE" : "not live"}
          </span>
          <button data-testid="numbers-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary">
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {accountNums.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your Telnyx numbers ({accountNums.length})</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="account-numbers">
            {accountNums.map((n) => (
              <span key={n.number} className="rounded-full bg-accent/60 px-2.5 py-1 font-mono-metric text-xs">{n.number}</span>
            ))}
          </div>
        </div>
      )}

      <datalist id="acct-numbers">
        {accountNums.map((n) => <option key={n.number} value={n.number} />)}
      </datalist>

      <div className="grid gap-4 lg:grid-cols-2">
        {ROLES.map((r) => (
          <div key={r.key} className="rounded-2xl border border-border bg-card p-4">
            <label className="block">
              <span className="text-sm font-semibold">{r.label}</span>
              <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{r.desc}</p>
              <input data-testid={`role-${r.key}`} list="acct-numbers" value={form[r.key] || ""}
                onChange={(e) => setForm({ ...form, [r.key]: e.target.value })}
                placeholder="+1XXXXXXXXXX (leave blank to use the deployment default)"
                className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            {r.key === "dialin_number" && (
              <label className="mt-3 flex w-max items-center gap-2 text-sm font-medium">
                <input data-testid="role-dialin-enabled" type="checkbox" checked={!!form.dialin_enabled}
                  onChange={(e) => setForm({ ...form, dialin_enabled: e.target.checked })} className="h-5 w-9 accent-primary" />
                Enable Meet dial-in
              </label>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button data-testid="numbers-save" onClick={save} disabled={busy}
          className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
          {busy ? "Saving…" : "Save number roles"}
        </button>
        <p className="text-xs text-muted-foreground">Blank = fall back to the deployment environment value.</p>
      </div>

      <SellableInventory telnyxLive={data.telnyx_live} />
      <E911Panel telnyxLive={data.telnyx_live} />
    </div>
  );
}

const E911_FIELDS = [
  { k: "first_name", label: "First name", req: true },
  { k: "last_name", label: "Last name", req: true },
  { k: "business_name", label: "Business name", req: true },
  { k: "street_address", label: "Street address", req: true },
  { k: "extended_address", label: "Suite / unit (optional)", req: false },
  { k: "locality", label: "City", req: true },
  { k: "administrative_area", label: "State / region", req: true },
  { k: "postal_code", label: "ZIP / postal code", req: true },
  { k: "country_code", label: "Country (ISO-2)", req: true },
];
const E911_STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-500",
  provisioning: "bg-amber-500/15 text-amber-500",
  "provisioning-failed": "bg-red-500/15 text-red-500",
  disabled: "bg-muted text-muted-foreground",
};

function E911Panel({ telnyxLive }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ number: "", country_code: "US" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data: d } = await api.get("/e911"); setData(d); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!form.number) return toast.error("Pick a number to register.");
    const missing = E911_FIELDS.filter((f) => f.req && !form[f.k]).map((f) => f.label);
    if (missing.length) return toast.error(`Fill: ${missing.join(", ")}`);
    setBusy(true);
    try {
      const { data: r } = await api.post("/e911/register", form);
      toast.success(`E911 ${r.emergency_status || "submitted"} for ${r.number}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const refreshStatus = async (number) => {
    try {
      const { data: r } = await api.get("/e911/status", { params: { number } });
      toast.success(`${number}: ${r.emergency_status}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const inputCls = "w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary";
  const regs = data?.registrations || [];
  const accountNums = data?.account_numbers || [];

  return (
    <div className="space-y-4 border-t border-border pt-6" data-testid="e911-panel">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-500/15 text-red-500"><Siren size={22} weight="fill" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">E911 Emergency Registration</h2>
          <p className="text-sm text-muted-foreground">Register a validated emergency address so your Cloud PBX lines are 911-compliant.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <label className="block max-w-sm">
          <span className="text-sm font-semibold">Number to register</span>
          <select data-testid="e911-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={`mt-1 ${inputCls}`}>
            <option value="">Select an owned number…</option>
            {accountNums.map((n) => <option key={n.number} value={n.number}>{n.number}</option>)}
          </select>
        </label>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {E911_FIELDS.map((f) => (
            <label key={f.k} className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{f.label}{f.req && " *"}</span>
              <input data-testid={`e911-${f.k}`} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className={`mt-1 ${inputCls}`} />
            </label>
          ))}
        </div>
        <button data-testid="e911-register" onClick={register} disabled={busy || !telnyxLive}
          className="mt-4 rounded-full bg-red-500 px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
          {busy ? "Validating & enabling…" : "Validate & enable E911"}
        </button>
        {!telnyxLive && <p className="mt-2 text-xs text-amber-500">Telnyx isn't live here — register E911 from the deployed app.</p>}
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Registered lines ({regs.length})</p>
        {regs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="e911-empty">No E911 registrations yet.</p>
        ) : (
          <div className="space-y-2" data-testid="e911-list">
            {regs.map((r) => (
              <div key={r.number} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2">
                <div className="min-w-0">
                  <span className="font-mono-metric text-sm">{r.number}</span>
                  <p className="truncate text-xs text-muted-foreground">{r.address_line}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${E911_STATUS_STYLE[r.emergency_status] || "bg-muted text-muted-foreground"}`}>{r.emergency_status || "unknown"}</span>
                  <button data-testid={`e911-refresh-${r.number}`} onClick={() => refreshStatus(r.number)} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold hover:border-primary">
                    <ArrowClockwise size={13} weight="bold" /> Status
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SellableInventory({ telnyxLive }) {
  const [inv, setInv] = useState([]);
  const [accountNums, setAccountNums] = useState([]);
  const [priceDollars, setPriceDollars] = useState("2.99");
  const [prefix, setPrefix] = useState("+12029809");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/number-inventory");
      setInv(data.inventory || []);
      setAccountNums(data.account_numbers || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const priceCents = () => Math.round(parseFloat(priceDollars || "0") * 100);

  const bulkAssign = async () => {
    const cents = priceCents();
    if (cents <= 0) return toast.error("Set a monthly price greater than 0.");
    setBusy(true);
    try {
      const { data } = await api.post("/number-inventory/bulk", { prefix, monthly_price_cents: cents });
      toast.success(`Assigned ${data.count} number${data.count === 1 ? "" : "s"} to sellable inventory.`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const assignSelected = async () => {
    const numbers = Object.keys(selected).filter((n) => selected[n]);
    const cents = priceCents();
    if (!numbers.length) return toast.error("Select at least one number.");
    if (cents <= 0) return toast.error("Set a monthly price greater than 0.");
    setBusy(true);
    try {
      const { data } = await api.post("/number-inventory/assign", { numbers, monthly_price_cents: cents });
      toast.success(`Assigned ${data.count} number${data.count === 1 ? "" : "s"}.`);
      setSelected({});
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setPrice = async (number) => {
    const val = window.prompt(`New monthly price for ${number} (USD):`, "2.99");
    if (val == null) return;
    const cents = Math.round(parseFloat(val || "0") * 100);
    if (cents <= 0) return toast.error("Price must be greater than 0.");
    try {
      await api.post("/number-inventory/price", { number, monthly_price_cents: cents });
      toast.success("Price updated.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (number) => {
    try {
      await api.post("/number-inventory/remove", { number });
      toast.success("Removed from sellable inventory.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const invNumbers = new Set(inv.map((i) => i.number));
  const assignable = accountNums.filter((n) => !invNumbers.has(n.number));
  const soldRows = inv.filter((i) => i.status === "sold");
  const availRows = inv.filter((i) => i.status !== "sold");
  const mrrCents = soldRows.reduce((s, i) => s + (i.monthly_price_cents || 0), 0);
  const availValueCents = availRows.reduce((s, i) => s + (i.monthly_price_cents || 0), 0);
  const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="space-y-4 border-t border-border pt-6" data-testid="sellable-inventory">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-500"><Storefront size={22} weight="fill" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Sellable Global Number Inventory</h2>
          <p className="text-sm text-muted-foreground">Turn owned numbers into monetizable inventory. The SI Sales engine offers these first, then live Telnyx search results.</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3" data-testid="inv-stats">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sold / paying lines</p>
          <p className="mt-1 font-mono-metric text-2xl font-semibold text-emerald-500" data-testid="inv-stat-sold">{soldRows.length}<span className="text-sm text-muted-foreground"> / {inv.length}</span></p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recurring revenue (MRR)</p>
          <p className="mt-1 font-mono-metric text-2xl font-semibold" data-testid="inv-stat-mrr">{money(mrrCents)}<span className="text-sm text-muted-foreground">/mo</span></p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Available to sell</p>
          <p className="mt-1 font-mono-metric text-2xl font-semibold" data-testid="inv-stat-avail">{availRows.length}<span className="text-sm text-muted-foreground"> · {money(availValueCents)}/mo</span></p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Monthly price (USD)</span>
            <input data-testid="inv-price" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)}
              className="mt-1 w-28 rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bulk prefix</span>
            <input data-testid="inv-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)}
              className="mt-1 w-40 rounded-xl border border-input bg-background px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
          </label>
          <button data-testid="inv-bulk-assign" onClick={bulkAssign} disabled={busy || !telnyxLive}
            className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? "Working…" : "Bulk assign matching numbers"}
          </button>
          <button data-testid="inv-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-xs font-semibold hover:border-primary">
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
        {!telnyxLive && <p className="mt-2 text-xs text-amber-500">Telnyx isn't live here, so account numbers can't be listed. Use the deployed app to bulk-assign.</p>}
      </div>

      {assignable.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Owned numbers not yet sellable ({assignable.length})</p>
          <div className="flex flex-wrap gap-2" data-testid="inv-assignable">
            {assignable.map((n) => (
              <label key={n.number} className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${selected[n.number] ? "border-primary bg-primary/10" : "border-border"}`}>
                <input type="checkbox" checked={!!selected[n.number]} onChange={(e) => setSelected({ ...selected, [n.number]: e.target.checked })} className="accent-primary" />
                <span className="font-mono-metric">{n.number}</span>
              </label>
            ))}
          </div>
          <button data-testid="inv-assign-selected" onClick={assignSelected} disabled={busy}
            className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            Assign selected at ${priceDollars}/mo
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sellable inventory ({inv.length})</p>
        {inv.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="inv-empty">No sellable numbers yet. Bulk-assign your spare numbers above.</p>
        ) : (
          <div className="space-y-2" data-testid="inv-list">
            {inv.map((i) => (
              <div key={i.number} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono-metric text-sm">{i.number}</span>
                  <span className="text-xs text-muted-foreground">{i.country} · {i.number_type}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${i.status === "sold" ? "bg-muted text-muted-foreground" : "bg-emerald-500/15 text-emerald-500"}`}>{i.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono-metric text-sm font-semibold">${(i.monthly_price_cents / 100).toFixed(2)}/mo</span>
                  {i.status !== "sold" && (
                    <>
                      <button data-testid={`inv-price-${i.number}`} onClick={() => setPrice(i.number)} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold hover:border-primary">
                        <Tag size={13} weight="bold" /> Price
                      </button>
                      <button data-testid={`inv-remove-${i.number}`} onClick={() => remove(i.number)} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-red-500 hover:border-red-500">
                        <Trash size={13} weight="bold" /> Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
